<template>
  <MeasurementCard
    :title="title"
    :info="info"
    :value="
      valueNumber !== undefined
        ? value?.type === 'gauge'
          ? {
              type: 'gauge',
              number: valueNumber[0],
              min: value?.min,
              max: value?.max,
              format: value?.format,
              isLoading: isNumberLoading ?? false
            }
          : {
              type: 'number',
              number: valueNumber[0],
              format: value?.format,
              textType: value?.textType,
              isLoading: isNumberLoading ?? false
            }
        : {
            type: 'string',
            string: '-',
            isLoading: isNumberLoading ?? false,
            textType: value && 'textType' in value ? value.textType : undefined
          }
    "
    :color="color"
    :colorScale="colorScale"
    :colorLegend="colorLegend"
    :coloredEntities="{
      ...coloredEntities,
      colorings: colorings.flat()
    }"
    :initialShowOnMap="initialShowOnMap"
  >
    <template v-if="table && table?.rows?.length" #details>
      <TableBasicTextLoader :table="table" :direction="'desc'" :caption="''" />
    </template>
  </MeasurementCard>
</template>
<script lang="ts" setup>
import MeasurementCard, { Coloring } from './MeasurementCard.vue'
import { computed, toRefs } from 'vue'
import { FormatOptions, formatter } from '../formatter'
import { ColorScaleConfig } from '@/utils/colorScale'
import { EntityLevel } from '@/composables/useEntityLevel'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { TextType } from '@/design-system/atoms/TextBase.vue'
import {
  useComponentFilter,
  useComponentFilterByResourceType
} from '../measurements/useMeasurements'
import { Feature } from '@/pinia/feature'
import TableBasicTextLoader, { BasicTable } from '@/design-system/atoms/TableBasicTextLoader.vue'
import { DashboardComponentType, Measurement } from '@/features/dashboard'
import { useComponentData } from '../measurements/useComponentData'
import { MeasurementPoint } from '../measurements/contracts'
import { useI18n } from 'vue-i18n'

export type ValueType = 'number' | 'gauge'

export type Value<Type extends ValueType = ValueType> = Type extends 'number'
  ? {
      type: 'number'
      dataSource: string
      textType?: TextType
      format?: FormatOptions
    }
  : {
      type: 'gauge'
      dataSource: string
      min?: number
      max?: number
      format?: FormatOptions
    }

export type ColoredEntities = {
  dataSource: string
  enabledEntityLevels?: EntityLevel[]
}

export type ColorLegend = {
  width?: number
  height?: number
  padding?: number
  hideMean?: boolean
  format?: FormatOptions
  labelStart?: LocaleString
  labelEnd?: LocaleString
}

export type TableConfig = {
  sort?: { col: number; direction: 'asc' | 'desc'; field: string }
  groups: string[]
}

type Props = {
  title: string
  info?: string
  measurement: Measurement
  value?: Value
  colorScale?: ColorScaleConfig
  color?: string
  coloredEntities?: ColoredEntities
  colorLegend?: ColorLegend
  initialShowOnMap?: boolean
  table?: TableConfig
  featureName: string
  children?: Feature[]
}

const props = withDefaults(defineProps<Props>(), {
  info: undefined,
  value: undefined,
  color: undefined,
  coloredEntities: undefined,
  initialShowOnMap: false,
  table: undefined,
  colorScale: undefined,
  children: () => [],
  colorLegend: () => ({
    height: 60,
    padding: 20,
    hideMean: false
  }),
  descriptionCaption: () => ({ sv: 'Detaljer', en: 'Details' })
})

const { value: valueConf, coloredEntities } = toRefs(props)
const { t, locale, te } = useI18n()

useComponentFilter(props.featureName, {})
useComponentFilterByResourceType(props.featureName, 'table', {
  query: {
    groupBy: props.table ? props.table.groups.map((group) => ({ type: 'tag', tag: group })) : []
  }
})

const { data: valueNumber, isLoading: isNumberLoading } = useComponentData<number>(
  props.featureName,
  DashboardComponentType.Kpi,
  'value'
)

const { data: colorings } = useComponentData<Coloring[]>(
  props.featureName,
  DashboardComponentType.Kpi,
  'colored-entities'
)

const { data: tableMeasurements, measurements } = useComponentData<MeasurementPoint[]>(
  props.featureName,
  DashboardComponentType.Kpi,
  'table'
)

const flatMeasurements = computed(() => tableMeasurements.value.flat())

const measurement = computed(() => measurements.value?.[0])
const field = computed(() => measurement.value?.field.name)

const getField = (measurement: MeasurementPoint) => {
  const f = measurement.fields[field.value]
  if (f === undefined) {
    return 0
  }
  return f as number
}

const table = computed<BasicTable | undefined>((): BasicTable | undefined => {
  return props.table
    ? {
        headers: [
          ...(props.table?.groups.map((group) => ({
            text: t(`general__data__${group}`),
            textAlign: 'left' as const
          })) ?? []),
          { text: t('general__text__value'), textAlign: 'right' as const }
        ],
        rows: flatMeasurements.value
          .toSorted(
            (a, b) =>
              (props.table?.sort?.direction === 'asc' ? 1 : -1) * (getField(a) - getField(b))
          )
          .map((measurement) => [
            ...props
              .table!.groups.map((group) => measurement.groups[group])
              .map((group) => ({
                text: group
                  ? te(`general__data__${group}`)
                    ? t(`general__data__${group}`)
                    : group.toString() // TODO: add better formating for this if we ever need it
                  : '-',
                textAlign: 'left' as const
              })),
            {
              text: formatter(
                measurement.fields[field.value] as number,
                locale.value,
                valueConf.value?.format
              ),
              textAlign: 'right' as const
            }
          ])
      }
    : undefined
})
</script>
